import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import CameraPanel from './CameraPanel';

const MAPBOX_TOKEN = "pk.eyJ1IjoiY29keXBvcmNoZSIsImEiOiJjbGJxeGRzdDYwb2RxM29udTFtNWVkZG05In0.RKVZ0CDS3Bq1u4IWCH7sDw";
const DEFAULT_CENTER = [-93.2174, 37.0902]; // (longitude, latitude) near geographic center of USA


export default function MapView({ cameras, setCameras, selectedCameraId, setSelectedCameraId }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null)
  const [addingCamera, setAddingCamera] = useState(false);
  const addingCameraRef = useRef(addingCamera);
  const [startCenter, setStartCenter] = useState(DEFAULT_CENTER);
  const cameraMarkersRef = useRef([]);

  useEffect(() => {
    addingCameraRef.current = addingCamera;
  }, [addingCamera]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setStartCenter([
            position.coords.longitude,
            position.coords.latitude
          ]);
        },
        () => {
          setStartCenter(DEFAULT_CENTER);
        }
      );
    }
  }, []);

  // Map initialization
  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_TOKEN;

    if (mapRef.current) {
      mapRef.current.setCenter(startCenter);
      return;
    }

    let timeout = setTimeout(() => {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: startCenter,
        zoom: 4.2, // Zoomed out to show the whole US
        pitch: 0,
        bearing: 0
      });
      mapRef.current = map;

      map.on('load', () => {
        map.resize();
        if (!map.getSource('fov-cones')) {
          map.addSource('fov-cones', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
          map.addLayer({
            id: 'fov-cones',
            type: 'fill',
            source: 'fov-cones',
            paint: {
              'fill-color': ['get', 'color'],
              'fill-opacity': 0.18
            }
          });
          map.addLayer({
            id: 'fov-cone-outline',
            type: 'line',
            source: 'fov-cones',
            paint: {
              'line-color': ['get', 'color'],
              'line-width': 2,
              'line-opacity': 0.5
            }
          });
        }
      });

      const geocoder = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl,
        marker: false
      });
      map.addControl(geocoder, 'top-left');

      map.on('click', (e) => {
        if (addingCameraRef.current) {
          const lngLat = [e.lngLat.lng, e.lngLat.lat];
          const newId = Date.now();
          setCameras(prev => [
            ...prev,
            {
              id: newId,
              name: `Camera ${prev.length + 1}`,
              lngLat,
              angle: 0,
              fov: 150,
              reach: 2000,
              color: '#00FF2A'
            }
          ]);
          setSelectedCameraId(newId); // Open panel for new camera
          setAddingCamera(false);
        }
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [setCameras, startCenter, setSelectedCameraId]);

  // Draw and update camera markers (with drag and color)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old markers
    if (cameraMarkersRef.current && cameraMarkersRef.current.length) {
      cameraMarkersRef.current.forEach(marker => marker.remove());
      cameraMarkersRef.current = [];
    }

    cameras.forEach((cam) => {
      // Create marker element with color and pointer cursor
      const el = document.createElement('div');
      el.style.width = '24px';
      el.style.height = '24px';
      el.style.background = cam.color || '#38bdf8';
      el.style.borderRadius = '50%';
      el.style.border = '2.5px solid white';
      el.style.boxShadow = '0 0 8px #0009';
      el.style.cursor = 'pointer';

      // Add marker click (open panel)
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedCameraId(cam.id);
      });

      // Create draggable marker
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(cam.lngLat)
        .addTo(map);

      // On drag end, update camera position
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        setCameras(prev =>
          prev.map(c =>
            c.id === cam.id
              ? { ...c, lngLat: [lngLat.lng, lngLat.lat] }
              : c
          )
        );
      });

      cameraMarkersRef.current.push(marker);
    });
  }, [cameras, setCameras, setSelectedCameraId]);

  // Draw/update all FOV cones (with color and REACH in FEET)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || !map.getSource('fov-cones')) return;

    const features = cameras.map(cam => {
      const [lng, lat] = cam.lngLat;
      const angle = cam.angle || 0;
      const fov = cam.fov || 150;
      const reachFeet = cam.reach || 500;
      const color = cam.color || '#38bdf8';

      // Convert feet to kilometers for turf
      const reachKm = reachFeet / 3280.84;

      const startBearing = angle - fov / 2;
      const endBearing = angle + fov / 2;
      const steps = 30;

      const coords = [[lng, lat]];
      for (let i = 0; i <= steps; i++) {
        const bearing = startBearing + (endBearing - startBearing) * (i / steps);
        const pt = turf.destination([lng, lat], reachKm, bearing, { units: 'kilometers' });
        coords.push(pt.geometry.coordinates);
      }
      coords.push([lng, lat]);

      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coords]
        },
        properties: {
          id: cam.id,
          color // used for FOV cone
        }
      };
    });

    const geojson = {
      type: 'FeatureCollection',
      features
    };

    map.getSource('fov-cones').setData(geojson);

  }, [cameras]);

  // -------- Panel helpers --------
  const selectedCamera = cameras.find(c => c.id === selectedCameraId) || null;
  const handleDelete = (id) => setCameras(prev => prev.filter(cam => cam.id !== id));
  const handleDuplicate = (cam) => setCameras(prev => [
    ...prev,
    { ...cam, id: Date.now(), name: cam.name + " (Copy)", lngLat: [...cam.lngLat] }
  ]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      minHeight: 400,
      position: 'relative'
    }}>
      <div
        ref={mapContainer}
        style={{ width: "100%", height: "100%" }}
      />
    <button
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        background: '#007AFF',     // iMessage blue
        color: 'white',
        padding: '8px',            // uniform padding
        borderRadius: 5,
        border: 'none',
        cursor: addingCamera ? 'not-allowed' : 'pointer',
        opacity: addingCamera ? 0.8 : 0.85,
        fontWeight: 600,
        fontSize: 14,
        boxShadow: '0 2px 8px #0004',
        zIndex: 10,
      }}
      onClick={() => setAddingCamera(true)}
      disabled={addingCamera}
    >
      {addingCamera ? 'Click map to place camera...' : '+ Add Camera'}
    </button>

      {/* CameraPanel overlays map */}
      <CameraPanel
        camera={selectedCamera}
        setCameras={setCameras}
        closePanel={() => setSelectedCameraId(null)}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
      />
    </div>
  );
}
